import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../services/api';
import {
    UserPlus, ShieldCheck, Power, Mail, Smartphone,
    Clock, History, Settings, Plus, X, Info, Calendar,
    ChevronRight, RefreshCw, Edit3, Trash2, FileText, User
} from 'lucide-react';
import './Admin.css';
import ConfirmModal from '../../components/ConfirmModal';


/**
 * AdminStudents — Advanced Student Management with Hours Tracking
 */
export default function AdminStudents() {
    const navigate = useNavigate();

    // Core state
    const [students, setStudents] = useState([]);
    const [instructors, setInstructors] = useState([]);
    const [allQuizzes, setAllQuizzes] = useState([]);
    const [allGroups, setAllGroups] = useState([]);
    const [loading, setLoading] = useState(true);

    // View state
    const [showModal, setShowModal] = useState(false);
    const [editHoursModal, setEditHoursModal] = useState({ show: false, student: null });
    const [historyModal, setHistoryModal] = useState({ show: false, student: null, sessions: [], loading: false });
    const [permModal, setPermModal] = useState({ show: false, student: null, allowedQuizzes: [] });

    // Confirm Modal & Alert state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'danger',
        singleButton: false
    });


    // Filters
    const [filters, setFilters] = useState({ groupId: '', groupCode: '', track: '' });

    // Form states
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        track: 'scratch',
        phone: '',
        groupName: '',
        groupId: '',
        groupCode: '',
        totalFees: '',
        totalHoursAllowed: 12,
        instructorId: '',
        remainingHours: 12
    });

    useEffect(() => {
        fetchData();
    }, [filters]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [usersData, quizData, groupData, instrData] = await Promise.all([
                api.getAllUsers({ role: 'student', ...filters }),
                api.getQuizzes(),
                api.getGroups(),
                api.getAllInstructors()
            ]);
            setStudents(usersData);
            setAllQuizzes(quizData);
            setAllGroups(groupData?.data || []);
            setInstructors(instrData?.data || []);
        } catch (err) {
            console.error('Fetch data error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateStudent = async (e) => {
        e.preventDefault();
        try {
            await api.createStudent(formData);
            setShowModal(false);
            resetForm();
            fetchData();
        } catch (err) {
            showAlert('Error', err.message || 'Error creating student', 'danger');
        }
    };

    const showAlert = (title, message, type = 'info') => {
        setConfirmModal({
            isOpen: true,
            title,
            message,
            type,
            singleButton: true,
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
        });
    };


    const resetForm = () => {
        setFormData({
            fullName: '', email: '', password: '', track: 'scratch', phone: '',
            groupName: '', groupId: '', groupCode: '', totalFees: '',
            totalHoursAllowed: 12, instructorId: '', remainingHours: 12
        });
    };

    // Open Edit Modal
    const handleOpenEdit = (student) => {
        setEditHoursModal({ show: true, student });
        setFormData({
            ...formData,
            totalHoursAllowed: student.totalHoursAllowed || 0,
            remainingHours: student.remainingHours || 0,
            instructorId: student.instructorId?._id || ''
        });
    };

    const handleUpdateHours = async (e) => {
        e.preventDefault();
        const { student } = editHoursModal;
        try {
            await api.updateUser(student._id, {
                totalHoursAllowed: parseFloat(formData.totalHoursAllowed),
                remainingHours: parseFloat(formData.remainingHours),
                instructorId: formData.instructorId || null
            });
            setEditHoursModal({ show: false, student: null });
            fetchData();
        } catch (err) {
            showAlert('Update Failed', err.message || 'Error updating student hours', 'danger');
        }
    };


    const handleOpenHistory = async (student) => {
        setHistoryModal({ show: true, student, sessions: [], loading: true });
        try {
            const res = await api.getStudentSessions(student._id);
            setHistoryModal(prev => ({ ...prev, sessions: res.data?.sessions || res.data || [], loading: false }));
        } catch (err) {
            showAlert('Error', 'Failed to load sessions', 'danger');
            setHistoryModal(prev => ({ ...prev, loading: false }));
        }
    };


    const handleRecalculate = (id) => {
        setConfirmModal({
            isOpen: true,
            title: 'Recalculate Hours',
            message: 'Are you sure you want to recalculate used hours from session logs? This will overwrite the current consumed hours value.',
            type: 'warning',
            singleButton: false,
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await api.recalculateStudentHours(id);
                    fetchData();
                    showAlert('Success', 'Hours synchronized successfully!', 'info');
                } catch (err) {
                    showAlert('Error', 'Recalculation failed', 'danger');
                }
            }
        });
    };


    const toggleUserStatus = async (id) => {
        try {
            await api.toggleUserStatus(id);
            fetchData();
        } catch (err) {
            showAlert('Error', 'Error toggling user status', 'danger');
        }
    };

    const handleDeleteStudent = (student) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Student Account',
            message: `Are you sure you want to permanently delete student "${student.fullName}"? This action cannot be undone.`,
            type: 'danger',
            singleButton: false,
            onConfirm: async () => {
                try {
                    await api.deleteUser(student._id);
                    setConfirmModal(prev => ({ ...prev, isOpen: false }));
                    fetchData();
                } catch (err) {
                    showAlert('Error', err.message || 'Failed to delete student', 'danger');
                }
            }
        });
    };



    const handleOpenPermissions = (student) => {
        setPermModal({ show: true, student, allowedQuizzes: student.allowedQuizzes || [] });
    };

    const toggleQuizPerm = (quizId) => {
        setPermModal(prev => {
            const current = prev.allowedQuizzes;
            const updated = current.includes(quizId) ? current.filter(id => id !== quizId) : [...current, quizId];
            return { ...prev, allowedQuizzes: updated };
        });
    };

    const handleSavePermissions = async () => {
        try {
            await api.updateStudentPermissions(permModal.student._id, { allowedQuizzes: permModal.allowedQuizzes });
            setPermModal({ show: false, student: null, allowedQuizzes: [] });
            fetchData();
        } catch (err) {
            showAlert('Error', 'Error saving permissions', 'danger');
        }
    };


    if (loading) return <div className="admin-loading-container"><div className="spinner"></div></div>;

    return (
        <div className="admin-page-container">
            <header className="admin-page-header">
                <div className="admin-page-header-text">
                    <h1>Student Directory</h1>
                    <p>Manage subscription packages, instructors, and access levels</p>
                </div>
                <button className="btn-action primary" onClick={() => { resetForm(); setShowModal(true); }}>
                    <UserPlus size={18} /> Onboard Student
                </button>
            </header>

            {/* Filters */}
            <div className="admin-filters-bar mb-6">
                <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 120px', gap: '1rem' }}>
                    <div className="cw-select-wrapper">
                        <select className="cw-select" value={filters.groupId} onChange={e => setFilters({ ...filters, groupId: e.target.value })}>
                            <option value="">All Groups</option>
                            {allGroups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                        </select>
                    </div>
                    <div className="cw-input-wrapper">
                        <input type="text" className="cw-input" placeholder="Filter by Job Code..." value={filters.groupCode} onChange={e => setFilters({ ...filters, groupCode: e.target.value })} />
                    </div>
                    <div className="cw-select-wrapper">
                        <select className="cw-select" value={filters.track} onChange={e => setFilters({ ...filters, track: e.target.value })}>
                            <option value="">All Tracks</option>
                            <option value="scratch">Scratch</option>
                            <option value="web">Web Dev</option>
                        </select>
                    </div>
                    <button className="btn-action secondary" onClick={() => setFilters({ groupId: '', groupCode: '', track: '' })}>Reset</button>
                </div>
            </div>

            <div className="admin-table-section-card">
                <div className="admin-table-wrapper">
                    <table className="admin-standard-table">
                        <thead>
                            <tr>
                                <th>Student Identity</th>
                                <th>Instructor & Group</th>
                                <th>Subscription Progress</th>
                                <th>Account Status</th>
                                <th style={{ textAlign: 'right' }}>Management</th>
                            </tr>
                        </thead>
                        <tbody>
                            {students.map(s => {
                                const remaining = s.remainingHours || 0;
                                const total = s.totalHoursAllowed || 0;
                                const consumed = s.consumedHours || 0;
                                const percent = total > 0 ? Math.min(Math.round((consumed / total) * 100), 100) : 0;
                                const isExpired = remaining <= 0 && total > 0;

                                return (
                                    <tr key={s._id}>
                                        <td>
                                            <div style={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                                <div className="student-avatar-sm">{s.fullName?.[0]}</div>
                                                <div onClick={() => navigate(`/admin/finance/student/${s._id}`)} style={{ cursor: 'pointer' }}>{s.fullName}</div>
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, marginTop: '2px' }}>ID: {s._id.slice(-6).toUpperCase()}</div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '4px' }}>
                                                <User size={12} color={s.instructorId ? "#2563eb" : "#94a3b8"} />
                                                <span style={{ fontSize: '0.85rem', color: s.instructorId ? '#0f172a' : '#94a3b8', fontWeight: s.instructorId ? 700 : 400 }}>
                                                    {s.instructorId?.fullName || "No Instructor"}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                {s.groupName || 'Unassigned Group'}
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '4px' }}>
                                                <span style={{ color: isExpired ? '#dc2626' : '#64748b' }}>
                                                    <strong>{remaining.toFixed(1)}h</strong> left
                                                </span>
                                                <span style={{ fontWeight: 700 }}>{consumed}/{total}h</span>
                                            </div>
                                            <div style={{ width: '100%', height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                                                <div style={{
                                                    width: `${percent}%`,
                                                    height: '100%',
                                                    background: isExpired ? '#dc2626' : percent > 80 ? '#f59e0b' : '#10b981',
                                                    transition: 'width 0.4s ease'
                                                }}></div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`status-pill ${s.isActive ? 'status-active' : 'status-inactive'}`}>
                                                {s.isActive ? 'Active Access' : 'Suspended'}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'end' }}>
                                                <button className="btn-action icon-only secondary" title="View Attendance" onClick={() => handleOpenHistory(s)}><History size={16} /></button>
                                                <button className="btn-action icon-only secondary" title="Edit Hours/Instructor" onClick={() => handleOpenEdit(s)}><Settings size={16} /></button>
                                                <button className="btn-action icon-only secondary" title="Permissions" onClick={() => handleOpenPermissions(s)}><ShieldCheck size={16} /></button>
                                                <button
                                                    className="btn-action icon-only secondary"
                                                    style={{ color: s.isActive ? '#dc2626' : '#15803d' }}
                                                    onClick={() => toggleUserStatus(s._id)}
                                                    title={s.isActive ? 'Disable Access' : 'Enable Access'}
                                                >
                                                    <Power size={16} />
                                                </button>
                                                <button
                                                    className="btn-action icon-only secondary"
                                                    style={{ color: '#dc2626' }}
                                                    onClick={() => handleDeleteStudent(s)}
                                                    title="Delete Student Account Permanently"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>

                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal: Onboard Student */}
            {showModal && (
                <div className="modal-overlay">
                    <div className="admin-table-section-card" style={{ width: '100%', maxWidth: '600px', padding: '2.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                            <h3 style={{ fontSize: '1.75rem', fontWeight: 800, margin: 0 }}>Student Onboarding</h3>
                            <button className="modal-close" onClick={() => setShowModal(false)}><X size={24} /></button>
                        </div>
                        <form onSubmit={handleCreateStudent} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                                <div>
                                    <label className="label-caps">Full Name</label>
                                    <input className="cw-input" required value={formData.fullName} onChange={e => setFormData({ ...formData, fullName: e.target.value })} />
                                </div>
                                <div>
                                    <label className="label-caps">Institutional Email</label>
                                    <input className="cw-input" type="email" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                                <div>
                                    <label className="label-caps">Access Password</label>
                                    <input className="cw-input" type="password" required value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} />
                                </div>
                                <div>
                                    <label className="label-caps">Primary Phone</label>
                                    <input className="cw-input" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                                </div>
                            </div>
                            <div className="form-grid-2">
                                <div>
                                    <label className="label-caps">Assigned Group</label>
                                    <select className="cw-select" value={formData.groupId} onChange={e => {
                                        const g = allGroups.find(x => x._id === e.target.value);
                                        setFormData({ ...formData, groupId: e.target.value, groupName: g?.name || '', groupCode: g?.code || '' });
                                    }}>
                                        <option value="">Unassigned</option>
                                        {allGroups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="label-caps">Assigned Instructor</label>
                                    <select className="cw-select" value={formData.instructorId} onChange={e => setFormData({ ...formData, instructorId: e.target.value })}>
                                        <option value="">No Instructor</option>
                                        {instructors.map(i => <option key={i._id} value={i._id}>{i.fullName}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                                <div>
                                    <label className="label-caps">Total Hours Package</label>
                                    <input className="cw-input" type="number" step="0.5" value={formData.totalHoursAllowed} onChange={e => setFormData({ ...formData, totalHoursAllowed: e.target.value })} />
                                </div>
                                <div>
                                    <label className="label-caps">Course Track</label>
                                    <select className="cw-select" value={formData.track} onChange={e => setFormData({ ...formData, track: e.target.value })}>
                                        <option value="scratch">Scratch Programming</option>
                                        <option value="web">Web Development</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'end', gap: '1rem', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #f1f5f9' }}>
                                <button type="button" className="btn-action secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-action primary">Initialize Account</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal: Edit Hours / Subscription */}
            {editHoursModal.show && (
                <div className="modal-overlay">
                    <div className="admin-table-section-card" style={{ width: '100%', maxWidth: '500px', padding: '2.5rem' }}>
                        <div style={{ borderLeft: '4px solid #2563eb', paddingLeft: '1rem', marginBottom: '2rem' }}>
                            <h3 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>Adjust Subscription</h3>
                            <p style={{ color: '#64748b' }}>Student: <strong>{editHoursModal.student.fullName}</strong></p>
                        </div>

                        <form onSubmit={handleUpdateHours} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div>
                                <label className="label-caps">Assign Instructor</label>
                                <select className="cw-select" value={formData.instructorId} onChange={e => setFormData({ ...formData, instructorId: e.target.value })}>
                                    <option value="">Unassigned</option>
                                    {instructors.map(i => <option key={i._id} value={i._id}>{i.fullName}</option>)}
                                </select>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                <div>
                                    <label className="label-caps">Total Allowed Hours</label>
                                    <input className="cw-input" type="number" step="0.5" value={formData.totalHoursAllowed} onChange={e => setFormData({ ...formData, totalHoursAllowed: e.target.value })} />
                                </div>
                                <div>
                                    <label className="label-caps">Manually Set Remaining</label>
                                    <input className="cw-input" type="number" step="0.1" value={formData.remainingHours} onChange={e => setFormData({ ...formData, remainingHours: e.target.value })} />
                                </div>
                            </div>

                            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                    <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Consumed Hours (Logged):</span>
                                    <span style={{ fontWeight: 800 }}>{editHoursModal.student.consumedHours}h</span>
                                </div>
                                <button type="button" className="btn-action secondary" style={{ width: '100%', height: '36px', fontSize: '0.75rem' }} onClick={() => handleRecalculate(editHoursModal.student._id)}>
                                    <RefreshCw size={14} /> Synchronize with Session Logs
                                </button>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'end', gap: '1rem', paddingTop: '1.5rem', borderTop: '1px solid #f1f5f9' }}>
                                <button type="button" className="btn-action secondary" onClick={() => setEditHoursModal({ show: false, student: null })}>Close</button>
                                <button type="submit" className="btn-action primary">Save Adjustments</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal: Session History */}
            {historyModal.show && (
                <div className="modal-overlay">
                    <div className="admin-table-section-card" style={{ width: '100%', maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <div>
                                <h3 style={{ fontSize: '1.75rem', fontWeight: 800, margin: 0 }}>Attendance Log</h3>
                                <p style={{ color: '#64748b' }}>Detailed history for {historyModal.student.fullName}</p>
                            </div>
                            <button className="modal-close" onClick={() => setHistoryModal({ show: false, student: null, sessions: [], loading: false })}><X size={24} /></button>
                        </div>

                        {/* Hours Summary */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '0.75rem', padding: '1rem', textAlign: 'center' }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Hours</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#0c4a6e' }}>{historyModal.student.totalHoursAllowed || 0}h</div>
                            </div>
                            <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: '0.75rem', padding: '1rem', textAlign: 'center' }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Consumed</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#78350f' }}>{historyModal.student.consumedHours || 0}h</div>
                            </div>
                            <div style={{ background: (historyModal.student.remainingHours || 0) <= 0 ? '#fee2e2' : '#dcfce7', border: `1px solid ${(historyModal.student.remainingHours || 0) <= 0 ? '#fecaca' : '#bbf7d0'}`, borderRadius: '0.75rem', padding: '1rem', textAlign: 'center' }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: (historyModal.student.remainingHours || 0) <= 0 ? '#991b1b' : '#166534', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Remaining</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 900, color: (historyModal.student.remainingHours || 0) <= 0 ? '#dc2626' : '#15803d' }}>{(historyModal.student.remainingHours || 0).toFixed(1)}h</div>
                            </div>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: '1rem' }}>
                            {historyModal.loading ? (
                                <div style={{ padding: '4rem', textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }}></div></div>
                            ) : historyModal.sessions.length === 0 ? (
                                <div style={{ padding: '4rem', textAlign: 'center', color: '#94a3b8' }}>
                                    <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
                                    <p>No recorded sessions found for this student.</p>
                                </div>
                            ) : (
                                <table className="admin-standard-table" style={{ minWidth: '100%' }}>
                                    <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                                        <tr>
                                            <th>Date</th>
                                            <th>Duration</th>
                                            <th>Instructor</th>
                                            <th>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyModal.sessions.map((session, idx) => (
                                            <tr key={idx}>
                                                <td>{new Date(session.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                                                <td><span style={{ background: '#eff6ff', color: '#2563eb', padding: '0.2rem 0.5rem', borderRadius: '4px', fontWeight: 700 }}>{session.durationInHours}h</span></td>
                                                <td>{session.instructorId?.fullName || "System"}</td>
                                                <td style={{ fontSize: '0.85rem', color: '#64748b', maxWidth: '250px' }}>{session.notes || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
                            <button className="btn-action secondary" onClick={() => setHistoryModal({ show: false, student: null, sessions: [], loading: false })}>Close Portal</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Permissions */}
            {permModal.show && (
                <div className="modal-overlay">
                    <div className="admin-table-section-card" style={{ width: '100%', maxWidth: '600px', padding: '2.5rem' }}>
                        <h3 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '0.5rem' }}>Protocol Permissions</h3>
                        <p style={{ fontSize: '0.95rem', color: '#64748b', marginBottom: '2rem' }}>Access control for: <strong>{permModal.student.fullName}</strong></p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem', maxHeight: '350px', overflowY: 'auto' }}>
                            {allQuizzes.map(q => (
                                <label key={q._id} style={{
                                    display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.25rem', border: '1px solid #e2e8f0',
                                    borderRadius: '1rem', cursor: 'pointer', background: permModal.allowedQuizzes.includes(q._id) ? '#f8fafc' : 'white'
                                }}>
                                    <input type="checkbox" style={{ width: '1.25rem', height: '1.25rem' }} checked={permModal.allowedQuizzes.includes(q._id)} onChange={() => toggleQuizPerm(q._id)} />
                                    <div>
                                        <p style={{ fontWeight: 800, color: '#0f172a', fontSize: '1rem' }}>{q.title}</p>
                                        <p style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: '#94a3b8', fontWeight: 900 }}>Track: {q.track}</p>
                                    </div>
                                </label>
                            ))}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'end', gap: '1rem', paddingTop: '1.5rem', borderTop: '1px solid #f1f5f9' }}>
                            <button className="btn-action secondary" onClick={() => setPermModal({ show: false, student: null, allowedQuizzes: [] })}>Discard</button>
                            <button className="btn-action primary" onClick={handleSavePermissions}>Commit Changes</button>
                        </div>
                    </div>
                </div>
            )}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.singleButton ? "Ok" : "Confirm"}
                cancelText={confirmModal.singleButton ? null : "Cancel"}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            />
        </div>

    );
}
